// Keep updated with SubGroupUserMembership in cryptoview/models/group.py
import {
  ApiTags,
  BotType,
  LongShort,
  MarginTypes,
  MarketplaceSubGroup,
  QueryBotType,
  StopType,
  TagName,
  TimeUnit,
} from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { Color } from "@shared/common/Themes/types";
import { ValueOf } from "@shared/types/utils";
import { invert } from "lodash";

export const BASE_DIGITAL_OCEAN_URL =
  "https://cornix.nyc3.digitaloceanspaces.com/";
export const BASE_SYMBOLS_ICONS_PATH = "icons/symbols/colored/";

export const BASE_EXCHANGES_ICONS_PATH = "icons/exchanges/";

export const TRADE_EXTRA_DETAILS_TABS = Object.freeze({
  GENERAL: "General",
  TIMELINE: "Timeline",
  ORDERS: "Orders",
  GRIDS: "Grids",
  CONFIGURATION: "Configuration",
});

export const TIME_RANGE_START = "start";
export const TIME_RANGE_1_HOUR = "1h";
export const TIME_RANGE_6_HOURS = "6h";
export const TIME_RANGE_12_HOURS = "12h";
export const TIME_RANGE_1_DAY = "1d";
export const TIME_RANGE_3_DAYS = "3d";
export const TIME_RANGE_1_WEEK = "1w";
export const TIME_RANGE_1_MONTHS = "1m";
export const TIME_RANGE_3_MONTHS = "3m";
export const TIME_RANGE_6_MONTHS = "6m";
export const TIME_RANGE_1_YEAR = "1y";
export const TIME_RANGES = [
  TIME_RANGE_START,
  TIME_RANGE_1_HOUR,
  TIME_RANGE_6_HOURS,
  TIME_RANGE_12_HOURS,
  TIME_RANGE_1_DAY,
  TIME_RANGE_3_DAYS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_6_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const BASE_CHART_TIME_RANGES = [
  TIME_RANGE_1_DAY,
  TIME_RANGE_3_DAYS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const EXTENDED_CHART_TIME_RANGES = [
  TIME_RANGE_1_HOUR,
  TIME_RANGE_6_HOURS,
  TIME_RANGE_12_HOURS,
  ...BASE_CHART_TIME_RANGES,
];

export const TRADING_VIEW_TIME_RANGES = [
  TIME_RANGE_1_HOUR,
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_1_YEAR,
];

export const TRADE_DRAWER_TIME_RANGES = [
  TIME_RANGE_START,
  ...TRADING_VIEW_TIME_RANGES,
];

export const DISPLAY_CURRENCIES = {
  BTC: "BTC",
  USD: "USD",
} as const;

export enum ICON_TYPES {
  EXCHANGE = "exchange",
  COIN = "coin",
  DISPLAY_CURRENCY = "displayCurrency",
}

export const AUTH_SLICE_PATH = "authentication";

// Breakpoints

export const SMALL_MOBILE = "450px";
export const MOBILE_SEARCH = "600px";
export const MOBILE_MENU = "900px";

export const MARGIN_TYPE_TO_DISPLAY: Record<MarginTypes, string> = {
  [MarginTypes.ISOLATED]: "Isolated",
  [MarginTypes.CROSS]: "Cross",
};

export const LONG_SHORT_TO_DISPLAY: Record<LongShort, string> = {
  [LongShort.LONG]: "Long",
  [LongShort.SHORT]: "Short",
};

export const STOP_TYPE_TO_DISPLAY: Record<StopType, string> = {
  [StopType.MARKET]: "Market",
  [StopType.LIMIT]: "Limit",
};

export const HOT_TRADES_HELPER_DESCRIPTION =
  "Hot trades are the trades that have the smallest distance (in percentage) to the next Entry / Take Profit / Stop";

export const ENTRY_ACTIVITY_IDS = [1, 6, 9];

export const TAKE_PROFIT_ACTIVITY_IDS = [2, 7, 10];

export const STOP_ACTIVITY_IDS = [3, 8, 11];
export const SECONDS_IN_MINUTE = 60;
export const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * 60;
export const SECONDS_IN_DAY = SECONDS_IN_HOUR * 24;
export const SECONDS_IN_WEEK = SECONDS_IN_DAY * 7;
export const SECONDS_IN_MONTH = SECONDS_IN_DAY * 30;
export const SECONDS_IN_YEAR = SECONDS_IN_MONTH * 12;
export const MINUTES_IN_HOUR = 60;
export const HOURS_IN_DAY = 24;
export const DAYS_IN_YEAR = 365;

export const TIME_RANGE_TO_SECONDS = {
  [TIME_RANGE_1_DAY]: SECONDS_IN_DAY,
  [TIME_RANGE_3_DAYS]: SECONDS_IN_DAY * 3,
  [TIME_RANGE_1_WEEK]: SECONDS_IN_WEEK,
  [TIME_RANGE_1_MONTHS]: SECONDS_IN_MONTH,
  [TIME_RANGE_3_MONTHS]: SECONDS_IN_MONTH * 3,
  [TIME_RANGE_1_YEAR]: SECONDS_IN_YEAR,
};

export const TIME_RANGE_SUFFIX = {
  MINUTE: "m",
  HOUR: "h",
  DAY: "D",
  WEEK: "W",
  MONTH: "M",
  YEAR: "Y",
};

export const ORDER_TYPE_TO_TITLE_DISPLAY = {
  [OrderTypes.ENTRY]: "Entry",
  [OrderTypes.TAKE_PROFIT]: "Take-Profit",
  [OrderTypes.STOP]: "Stop",
};

export const INITIAL_DATA_MAX_TIME_SECONDS = 100000000 * 60;

// When updating this constant, make sure to also update derived arrays like LEVERAGE_EXCHANGES if needed
export const EXCHANGES = {
  BINANCE: "Binance",
  BINANCE_FUTURES: "Binance Futures",
  BINANCE_COIN_FUTURES: "Binance Coin-Futures",
  BINANCE_CROSS_MARGIN: "Binance Cross-Margin",
  BYBIT_SPOT: "ByBit Spot",
  BYBIT_USDT: "ByBit USDT",
  BYBIT_INVERSE: "ByBit",
  OKX: "OKX",
  OKX_FUTURES: "OKX Futures",
  OKX_INVERSE: "OKX Inverse",
  BITMEX: "BitMEX",
  KUCOIN: "KuCoin",
  KUCOIN_FUTURES: "KuCoin Futures",
  BITTREX: "Bittrex",
  HUOBI: "Huobi.pro",
  FTX: "FTX",
  FTX_FUTURES: "FTX Futures",
  BITGET_SPOT: "Bitget Spot",
  BITGET_FUTURES: "Bitget Futures",
  COINBASE_SPOT: "Coinbase Advanced Spot",
  COINBASE_FUTURES: "Coinbase Advanced Futures",
  HYPERLIQUID_SPOT: "HyperLiquid Spot",
  HYPERLIQUID_FUTURES: "HyperLiquid Futures",
} as const;

export const PRIMARY_EXCHANGES: Record<string, string> = {
  BINANCE: "Binance",
  BYBIT: "ByBit",
  BITMEX: "BitMEX",
  HUOBI: "Huobi.pro",
  KUCOIN: "KuCoin",
  OKX: "OKX",
  BITGET: "Bitget",
  COINBASE: "Coinbase",
  HYPERLIQUID: "HyperLiquid",
} as const;

export const LEVERAGE_EXCHANGES: Set<string> = new Set([
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BINANCE_CROSS_MARGIN,
  EXCHANGES.BYBIT_USDT,
  EXCHANGES.BYBIT_INVERSE,
  EXCHANGES.OKX_FUTURES,
  EXCHANGES.OKX_INVERSE,
  EXCHANGES.BITMEX,
  EXCHANGES.KUCOIN_FUTURES,
  EXCHANGES.FTX_FUTURES,
  EXCHANGES.BITGET_FUTURES,
  EXCHANGES.COINBASE_FUTURES,
  EXCHANGES.HYPERLIQUID_FUTURES,
]);

export const CONTRACTS_EXCHANGES: string[] = [
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BYBIT_INVERSE,
  EXCHANGES.BITMEX,
];

export const HYPERLIQUID_EXCHANGES = new Set<string>([
  EXCHANGES.HYPERLIQUID_SPOT,
  EXCHANGES.HYPERLIQUID_FUTURES,
]);

export const BINANCE_EXCHANGES = new Set<string>([
  EXCHANGES.BINANCE,
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_CROSS_MARGIN,
  EXCHANGES.BINANCE_COIN_FUTURES,
]);

export const MAX_LEVERAGE_CROSS_EXCHANGES: string[] = [
  EXCHANGES.BITMEX,
  EXCHANGES.BYBIT_SPOT,
];

export const QUICK_CONNECT_EXCHANGES = new Set<string>([
  PRIMARY_EXCHANGES.BINANCE,
  PRIMARY_EXCHANGES.BYBIT,
  PRIMARY_EXCHANGES.OKX,
  PRIMARY_EXCHANGES.BITGET,
  PRIMARY_EXCHANGES.KUCOIN,
]);

export const HEDGE_MODE_EXCHANGES = new Set<string>([
  EXCHANGES.BINANCE_FUTURES,
  EXCHANGES.BINANCE_COIN_FUTURES,
  EXCHANGES.BYBIT_USDT,
  EXCHANGES.BITGET_FUTURES,
  EXCHANGES.BYBIT_USDT,
]);

export const DOLLAR_COINS = [
  "USD",
  "USDT",
  "BUSD",
  "TUSD",
  "USDC",
  "DAI",
  "USDP",
];

export const SIGNAL_EXTRA_DETAILS_TABS = Object.freeze({
  TIMELINE: "Timeline",
  TARGETS: "Targets",
  GENERAL: "General",
  CONFIGURATION: "Configuration",
});

export const MOBILE_SIGNAL_EXTRA_DETAILS_TABS = Object.freeze({
  TIMELINE: "Timeline",
  TARGETS: "Targets",
  GENERAL: "General",
  CONFIGURATION: "Configuration",
  TRADES: "Trades",
});

export const ENTRY_UUID = "Create Trade UUID";
export const TAKE_PROFIT_UUID = "Close Trades UUID";
export const DISTANCE_FROM_TRIGGER = ["Distance from", "First Entry (Trigger)"];

export const VIRTUAL_CARDS_LIST_PADDING = 20;

export const REGISTERED_EMAIL = "registered_email";
export const EMAIL_UUID_QUERY_PARAM = "email-uuid";
export const SUPPORT_BOT_LINK = "https://t.me/cornix_tech_support_bot";
export const LOGIN_UNIQUE_ID_STORAGE_KEY = "login_unique_id";

export const IS_NUMERIC_REGEX = /^-?\d+$/;
export const SHORT_UUID_REGEX = /^[0-9A-F]{8}$/;
export const FLOAT_NUMBER_REGEX =
  /[+\-]?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+\-]?\d+)?/;
export const UUID_IN_USE_CODE = 409;
export const LOCAL_STORAGE_REGISTRATION_DATA_KEY = "registration_data";
export const STORAGE_OAUTH_DATA_KEY = "oauth_data";
export const STORAGE_OAUTH_TEMP_DATA_KEY = "oauth_temp_data";

export const BINANCE_FUTURES_LINK = "https://www.binance.com/en/futures";
export const BINANCE_API_MANAGEMENT_LINK =
  "https://www.binance.com/en/my/settings/api-management";
export const OKX_API_MANAGEMENT_LINK = "https://www.okx.com/account/my-api";
export const BYBIT_API_MANAGEMENT_LINK =
  "https://www.bybit.com/app/user/api-management";
export const KUCOIN_API_MANAGEMENT_LINK = "https://www.kucoin.com/account/api";

export const ONBOARDING_STEP_TO_TITLE: Record<string, string> = {
  [OnboardingDetailedStep.SELECT_EXCHANGE]: "Select Exchange",
  [OnboardingDetailedStep.CONNECT_EXCHANGE]: "Connect Your Exchange",
  [OnboardingDetailedStep.TRADING_METHOD]: "Trading Method",
  [OnboardingDetailedStep.COPY_DCA_BOT]: "Method Settings",
  [OnboardingDetailedStep.DCA_BOT_OVERVIEW]: "Overview",
  [OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP]: "Method Settings",
  [OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW]: "Overview",
  [OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT]: "Method Settings",
};
export const PASSWORD_REGEX =
  /^(?=.*\d)(?=.*[!@#$%^&*()\-=+\[\]{}<>_?~;:,.\\/"'])(?=.*[a-z])(?=.*[A-Z]).{8,99}$/;
export const EIGHT_SYMBOLS_REGEX = /^.{8,}$/;
export const SPECIAL_SYMBOL_REGEX = /[!@#$%^&*()\-=+\[\]{}<>_?~;:,.\\"']+/;
export const UPPER_CASE_REGEX = /^(?=.*[A-Z]).*$/;
export const LOWER_CASE_REGEX = /^(?=.*[a-z]).*$/;
export const NUMBER_REGEX = /[0-9]+/;

export const INTERCOM_ARTICLE_ID = /\/(\d+)[^d]/;
export const UUID_ALREADY_CONNECTED_ARTICLE_ID = "6175288";

export const TERMS_OF_USE_LINK = "https://cornix.io/terms";
export const AFFILIATION_AGREEMENT_TERMS_LINK =
  "https://cornix.io/affiliation_terms";
export const PRIVACY_POLICY_LINK = "https://cornix.io/privacy";

export const EMPTY_GROUPS_TITLE =
  "No Telegram or Discord groups connected to Cornix";
export const EMPTY_GROUPS_DESCRIPTION =
  "Check out our marketplace to find a group and start getting signals";
export const EMPTY_SIGNALS_DESCRIPTION =
  "Check our Marketplace to find a group and get more signals";

export const SIGNAL_URL_KEY = "signal";
export const SUB_GROUP_URL_KEY = "sub_group";
export const REGISTRATION_REF_URL_KEY = "ref";

export const BINANCE_AUTH_PATH = "/auth/binance";
export const BYBIT_AUTH_PATH = "/auth/bybit";
export const OKX_AUTH_PATH = "/auth/okx";
export const BITGET_AUTH_PATH = "/auth/bitget";
export const KUCOIN_AUTH_PATH = "/auth/kucoin";

export const TagNameToColor: Record<TagName, Color> = {
  [TagName.TOP_SELLER]: "primaryBlue",
  [TagName.NEW]: "green",
  [TagName.SALE]: "gold",
  [TagName.FREE]: "pink",
  [TagName.FREE_TRIAL]: "purple",
};

export const TAG_NAME_TO_PROPERTY_NAME: Record<
  TagName,
  keyof MarketplaceSubGroup
> = {
  [TagName.NEW]: "is_new",
  [TagName.SALE]: "discount",
  [TagName.TOP_SELLER]: "is_best_seller",
  [TagName.FREE]: "is_free",
  [TagName.FREE_TRIAL]: "has_free_trial",
};

export const NEW_IP_LOGIN_TITLE = "New IP Address Detected";
export const NEW_IP_LOGIN_DESCRIPTION =
  "An email has been sent to your registered email. Please check your inbox and follow the instructions provided to complete the verification process.";

export const TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME = {
  [OrderTypes.ENTRY]: "Entry",
  //Using a weird 'e' for the word 'take' - do not touch!!!
  [OrderTypes.TAKE_PROFIT]: "Takе-Profit",
  [OrderTypes.STOP]: "Stop",
};

export const ACCESS_TOKEN_STORAGE_KEY = "access_token";
export const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
export const IS_MOBILE_WEBVIEW_WINDOW_KEY = "is_mobile_view";

export const EXCHANGES_ORDER: Record<ValueOf<typeof EXCHANGES>, number> = {
  [EXCHANGES.BINANCE]: 1,
  [EXCHANGES.BINANCE_FUTURES]: 2,
  [EXCHANGES.BINANCE_COIN_FUTURES]: 3,
  [EXCHANGES.BINANCE_CROSS_MARGIN]: 4,
  [EXCHANGES.BYBIT_SPOT]: 5,
  [EXCHANGES.BYBIT_USDT]: 6,
  [EXCHANGES.BYBIT_INVERSE]: 7,
  [EXCHANGES.OKX]: 8,
  [EXCHANGES.OKX_FUTURES]: 9,
  [EXCHANGES.OKX_INVERSE]: 10,
  [EXCHANGES.BITGET_SPOT]: 11,
  [EXCHANGES.BITGET_FUTURES]: 12,
  [EXCHANGES.KUCOIN]: 13,
  [EXCHANGES.KUCOIN_FUTURES]: 14,
  [EXCHANGES.HYPERLIQUID_SPOT]: 15,
  [EXCHANGES.HYPERLIQUID_FUTURES]: 16,
  [EXCHANGES.COINBASE_SPOT]: 17,
  [EXCHANGES.COINBASE_FUTURES]: 18,
  [EXCHANGES.BITMEX]: 19,
  [EXCHANGES.HUOBI]: 20,
  [EXCHANGES.BITTREX]: 21,
  [EXCHANGES.FTX]: 22,
  [EXCHANGES.FTX_FUTURES]: 23,
};

export const EXCHANGES_UNIQUE_IDS: Record<ValueOf<typeof EXCHANGES>, number> = {
  [EXCHANGES.BYBIT_INVERSE]: 1,
  [EXCHANGES.BINANCE_FUTURES]: 2,
  [EXCHANGES.BINANCE_COIN_FUTURES]: 3,
  [EXCHANGES.BINANCE]: 4,
  [EXCHANGES.BITMEX]: 5,
  [EXCHANGES.KUCOIN]: 6,
  [EXCHANGES.BITTREX]: 7,
  [EXCHANGES.HUOBI]: 8,
  [EXCHANGES.FTX_FUTURES]: 11,
  [EXCHANGES.FTX]: 12,
  [EXCHANGES.BYBIT_USDT]: 13,
  [EXCHANGES.BINANCE_CROSS_MARGIN]: 14,
  [EXCHANGES.BYBIT_SPOT]: 15,
  [EXCHANGES.OKX]: 16,
  [EXCHANGES.OKX_FUTURES]: 17,
  [EXCHANGES.OKX_INVERSE]: 18,
  [EXCHANGES.KUCOIN_FUTURES]: 19,
  [EXCHANGES.BITGET_FUTURES]: 20,
  [EXCHANGES.BITGET_SPOT]: 22,
  [EXCHANGES.COINBASE_SPOT]: 23,
  [EXCHANGES.HYPERLIQUID_SPOT]: 24,
  [EXCHANGES.HYPERLIQUID_FUTURES]: 25,
  [EXCHANGES.COINBASE_FUTURES]: 26,
};

export const SUBSCRIBE_GROUP_REFETCH_ENDPOINTS_LIST = [
  { type: ApiTags.GROUP_DATA, id: "LIST" },
  { type: ApiTags.CLIENTS, id: "LIST" },
  { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
  { type: ApiTags.AUTO_TRADING_GROUPS, id: "LIST" },
  { type: ApiTags.SIGNALS_SUB_GROUPS, id: "LIST" },
];

export const UNSAVED_CHANGES_WARNING_MESSAGE =
  "You have unsaved changes. Proceeding with this action will discard them unless you save your changes first.\nAre you sure you wish to proceed and discard your changes?";

export const SHOW_DELETE_USER_BUTTON_FEATURE_FLAG = "show_delete_user_button";

export const LOCAL_STORAGE_UTM_DATA_KEY = "utm_data";

export const UUID_REGEX =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

export let IS_NET_CONNECTED = true;
export const setIsOnline = (isOnline: boolean) => {
  IS_NET_CONNECTED = isOnline;
};

export const TIME_UNITS_ORDER = [
  TimeUnit.YEAR,
  TimeUnit.DAY,
  TimeUnit.HOUR,
  TimeUnit.MINUTE,
  TimeUnit.SECOND,
];

export const TIME_UNIT_TO_MULTIPLIER = {
  [TimeUnit.YEAR]: DAYS_IN_YEAR,
  [TimeUnit.DAY]: HOURS_IN_DAY,
  [TimeUnit.HOUR]: MINUTES_IN_HOUR,
  [TimeUnit.MINUTE]: SECONDS_IN_MINUTE,
  [TimeUnit.SECOND]: 0,
};

export const LIFETIME_DATE = "12/31/9999";
export const WORKSPACE_STORAGE_KEY = "workspace";
export const ENV_TYPE_QUERY_PARAM = "env-type";

export const QUERY_BOT_TYPE_TO_BOT_TYPE: Record<QueryBotType, BotType> = {
  [QueryBotType.ASAP]: BotType.ASAP,
  [QueryBotType.TRADING_VIEW]: BotType.TRADING_VIEW,
  [QueryBotType.SIGNAL]: BotType.SIGNAL,
  [QueryBotType.GRID]: BotType.GRID,
};

export const BOT_TYPE_TO_QUERY_BOT_TYPE = invert(
  QUERY_BOT_TYPE_TO_BOT_TYPE
) as Record<BotType, QueryBotType>;

export const LEVERAGE_EXCHANGES_TAG = "leverage_exchanges";
export const NON_LEVERAGE_EXCHANGES_TAG = "non_leverage_exchanges";
export const MAX_ACCOUNT_NAME_LENGTH = 32;

export const FIRST_ENTRY_AS_MARKET_FEATURE_FLAG = "first_entry_as_market";
export const MOVING_TAKE_PROFIT_FEATURE_FLAG =
  "moving_take_profits_first_entry";
export const ALTERNATIVE_USD_PAIRS_FEATURE_FLAG = "alternative_usd_pairs";
export const NEW_SYMBOLS_FEATURE_FLAG = "new_symbols";
export const CHANGE_FULL_NAME_FEATURE_FLAG = "change_full_name";
export const TAKE_PROFIT_GRACE_FEATURE_FLAG = "take_profit_grace";
export const REQUESTS_AUTH_HEADER = "Authorization";
